import * as React from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Collapse,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  FormGroup,
  Checkbox,
  Button,
} from "@mui/material";
import { faSquareCheck, faFileExport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

import { getMatchDetailFullExport } from "../../api/index.js";
import useStyles from "../../styles/global-mui.js";
import DsasButton from "../../v1/components/DsasButton/DsasButton.js";
import { faSquare } from "@fortawesome/free-regular-svg-icons";
import { getUser, isAdminDSAS } from "../../actions/auth.js";
import { downloadLive } from "../../v1/pages/direct/utils/downloadLive.js";

const LiveExport = ({ idLive, isActu, live }) => {
  const classes = useStyles();

  //console.log("LiveExport props:", { idLive, isActu, live }); 

  const [open, setOpen] = React.useState(false);
  const [selectedTags, setSelectedTags] = React.useState([]);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleTagToggle = (tagId) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  const getURL = () => {
    let urlLocation;
    if (process.env.NODE_ENV === "development") {
      urlLocation = "http://localhost:3006";
    } else {
      urlLocation = process.env.REACT_APP_LIVE_CLIENT_URL;
    }

    let url = isActu
      ? `${urlLocation}/actualite/${idLive}`
      : `${urlLocation}/${idLive}`;

    if (selectedTags.length > 0) {
      url += `?tags=${selectedTags.join(",")}`;
    }

    return url;
  };

  return (
    <>
      <Box className={classes.actionButtonIcon} onClick={handleClickOpen}>
        <Typography className={classes.responsiveText}>Export</Typography>
        <FontAwesomeIcon icon={faFileExport} />
      </Box>

      <SimpleDialog
        open={open}
        onClose={handleClose}
        url={getURL()}
        isActu={isActu}
        idLive={idLive}
        live={live}
        tags={isActu && live?.actualitesDetails?.[0]?.tags ? live.actualitesDetails[0].tags : []}
        selectedTags={selectedTags}
        onTagToggle={handleTagToggle}
      />
    </>
  );
};

function SimpleDialog({ onClose, open, url, isActu, idLive, live, tags, selectedTags, onTagToggle }) {
  const [width, setWidth] = React.useState(600);
  const [height, setHeight] = React.useState(900);

  // advanced options & custom button
  const [hasCustomButton, setHasCustomButton] = React.useState(false);
  const [redirectUrl, setRedirectUrl] = React.useState("");
  const [displayText, setDisplayText] = React.useState("");
  const [buttonPosition, setButtonPosition] = React.useState("top");

  // Export Word (no nested Dialog)
  const EXPORT_TYPES = ["article", "survey", "quizz", "questionBox", "notation"];
  const EXPORT_TYPE_LABELS = {
    all: "Tout",
    article: "Articles",
    survey: "Sondages",
    quizz: "Quiz",
    questionBox: "Boîtes à questions",
    notation: "Notations",
  };

  const [exportOpen, setExportOpen] = React.useState(false);
  const [exportSelection, setExportSelection] = React.useState({
    all: true,
    article: true,
    survey: true,
    quizz: true,
    questionBox: true,
    notation: true,
  });

  const handleExportCheckbox = (key) => (e) => {
    const checked = e.target.checked;

    if (key === "all") {
      setExportSelection({
        all: checked,
        article: checked,
        survey: checked,
        quizz: checked,
        questionBox: checked,
        notation: checked,
      });
      return;
    }

    const next = { ...exportSelection, [key]: checked };
    next.all = EXPORT_TYPES.every((t) => next[t]);
    setExportSelection(next);
  };

  const getSelectedExportTypes = () => {
    if (exportSelection.all) return ["all"];
    return EXPORT_TYPES.filter((t) => exportSelection[t]);
  };

  const onExportWord = async () => {
    const selectedTypes = getSelectedExportTypes();

    if (!selectedTypes.length) {
      alert("Sélectionne au moins un type à exporter.");
      return;
    }

    const idActu = isActu && live?.actualites ? live.actualites[0] : ''; // Passer l'idActu uniquement si c'est une actu

    if (idActu) {

      try {
        const response = await getMatchDetailFullExport(idActu);

        if (!response || !response.data) {
          throw new Error("Réponse invalide de getMatchDetailFullExport");
        }

        const user = getUser?.();
        const userGroup = Array.isArray(user?.groups)
          ? user.groups.join("-")
          : user?.groups || "";

        await downloadLive(response.data, live?.name, selectedTypes, { userGroup });
        setExportOpen(false);
      } catch (e) {
        console.error("Erreur export Word:", e);
        alert("Échec du téléchargement du document.");
      }
    } else {
      alert("Aucun contenu à exporter pour cette actualité.");
    }
  };

  const handleClose = () => onClose();

  const getFinalUrl = React.useCallback(() => {
    if (!hasCustomButton || !redirectUrl || !displayText) {
      return url;
    }

    const separator = url.includes("?") ? "&" : "?";
    const params = new URLSearchParams({
      "redirect-url": redirectUrl,
      "display-text": displayText,
      "button-position": buttonPosition || "top",
    });

    return `${url}${separator}${params.toString()}`;
  }, [url, hasCustomButton, redirectUrl, displayText, buttonPosition]);

  const isButtonDisabled = React.useCallback(() => {
    const invalidSize = isNaN(width) || isNaN(height);
    const invalidCustomButton = hasCustomButton && (!redirectUrl || !displayText);
    return invalidSize || invalidCustomButton;
  }, [width, height, hasCustomButton, redirectUrl, displayText]);

  const onCopyIframeSmartScroll = () => {
    const finalUrl = getFinalUrl();
    if (navigator.clipboard && finalUrl) {
      const responsiveIframe =
        '<script src="https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.6/iframeResizer.min.js"></script>' +
        '<script>if(!window._rsz){window._rsz = function () {' +
        "let scrollPending = false;" +
        "let lastIframe = null;" +
        "iFrameResize({" +
        "checkOrigin: false," +
        "interval: 100," +
        "messageCallback: function(messageData) {" +
        "const message = messageData.message;" +
        "const iframe = messageData.iframe;" +
        "if (!message) return;" +
        "if (message.type === \"scrollToTop\") {" +
        "scrollPending = true;" +
        "lastIframe = iframe;" +
        "}" +
        "if (message.type === \"scrollToTopReady\" && scrollPending && iframe === lastIframe) {" +
        "setTimeout(() => {" +
        "window.scrollTo({" +
        "top: iframe.offsetTop," +
        "behavior: \"smooth\"" +
        "});" +
        "scrollPending = false;" +
        "lastIframe = null;" +
        "}, 50);" +
        "}" +
        "}" +
        "});" +
        '};if(document.readyState!="loading"){_rsz()}else{document.addEventListener("DOMContentLoaded",_rsz)}}<\/script>' +
        '<iframe src="' +
        finalUrl +
        '" style="display: block; width: 100%; overflow: auto; margin: auto; border-width: 0px;" scrolling="no"></iframe>';

      navigator.clipboard.writeText(responsiveIframe);
    }
  };

  const onCopyIframe = () => {
    const finalUrl = getFinalUrl();
    if (navigator.clipboard && finalUrl) {
      const responsiveIframe =
        '<script src="https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.6/iframeResizer.min.js"></script>' +
        '<script>if(!window._rsz){window._rsz=function(){var i=iFrameResize({checkOrigin:false,interval:100});};if(document.readyState!="loading"){_rsz()}else{document.addEventListener("DOMContentLoaded",_rsz)}}</script>' +
        '<iframe src="' +
        finalUrl +
        '" style="display: block; width: 100%; overflow: auto; margin: auto; border-width: 0px;" scrolling="no"></iframe>';

      navigator.clipboard.writeText(responsiveIframe);
    }
  };

  const onCopyURL = () => {
    const finalUrl = getFinalUrl();
    if (navigator.clipboard && finalUrl) {
      navigator.clipboard.writeText(finalUrl);
    }
  };

  const classes = useStyles();

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      className={classes.customPopup}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(109, 110, 115, 0.6)",
        },
      }}
    >
      {/* Close button */}
      <Box
        sx={{
          position: "absolute",
          top: "1rem",
          right: "1rem",
          width: "16px",
          height: "16px",
          borderRadius: "50%",
          border: "2px solid var(--color-blue-secondary)",
          backgroundColor: "var(--color-white)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          zIndex: 1,
          "&:hover": { backgroundColor: "var(--color-white)" },
        }}
        onClick={handleClose}
      >
        <Box
          component="span"
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: 1,
            color: "var(--color-blue-secondary)",
          }}
        >
          ×
        </Box>
      </Box>

      <Box alignItems="center" textAlign={"center"} className="medium-padding">
        <Typography variant="h2" className="popupTitle">
          Paramètres
        </Typography>

        {isActu && tags.length > 0 && (
          <Box sx={{ my: 2 }}>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(0, 0, 0, 0.6)",
                fontWeight: "400",
                fontSize: "0.8rem",
                textAlign: "left",
                paddingLeft: "16px",
              }}
            >
              Tags
            </Typography>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {tags.map((tag) => {
                const checked = selectedTags.includes(tag._id);
                return (
                  <Box
                    key={tag._id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      gap: 1,
                      mb: 1,
                      mr: 2,
                    }}
                    onClick={() => onTagToggle(tag._id)}
                  >
                    <FontAwesomeIcon
                      icon={checked ? faSquareCheck : faSquare}
                      style={{
                        color: "var(--color-blue-secondary)",
                        fontSize: "1.2rem",
                      }}
                    />
                    <Typography variant="body2">{tag.name}</Typography>
                  </Box>
                );
              })}
            </Box>

            <Box className="large-separator small-padding" sx={{ pl: 2, pr: 2 }}></Box>
          </Box>
        )}
      </Box>

      <Box className="large-separator"></Box>

      <Box className="medium-padding">
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Options avancées
        </Typography>

        <Box className="medium-padding" sx={{ pl: 2, pr: 2, pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasCustomButton}
                    onChange={(e) => setHasCustomButton(e.target.checked)}
                    color="primary"
                  />
                }
                label="Homepage"
              />
            </Grid>

            {hasCustomButton && (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Intitulé du bouton de redirection"
                    variant="standard"
                    fullWidth
                    value={displayText}
                    onChange={(e) => setDisplayText(e.target.value)}
                    sx={{
                      "& .MuiInput-underline:before": {
                        borderBottom: "1px solid var(--color-light-grey)",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottom: "1px solid var(--color-medium-grey)",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="URL de redirection du bouton"
                    variant="standard"
                    placeholder="https://www.monsite.fr/ma-page"
                    fullWidth
                    value={redirectUrl}
                    onChange={(e) => setRedirectUrl(e.target.value)}
                    sx={{
                      "& .MuiInput-underline:before": {
                        borderBottom: "1px solid var(--color-light-grey)",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottom: "1px solid var(--color-medium-grey)",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl sx={{ mt: 2 }}>
                    <FormLabel>Position du bouton</FormLabel>
                    <RadioGroup
                      row
                      value={buttonPosition}
                      onChange={(e) => setButtonPosition(e.target.value)}
                    >
                      <FormControlLabel value="top" control={<Radio />} label="Haut de page" />
                      <FormControlLabel value="bottom" control={<Radio />} label="Bas de page" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Box>

      <Box className="large-separator"></Box>

      <Box className="centered-wrapper">
        <DsasButton
          variant="secondary"
          label="Copier en tant qu'IFRAME"
          onClick={onCopyIframe}
          disabled={isButtonDisabled()}
        />
      </Box>

      {isAdminDSAS() && (
        <Box className="centered-wrapper">
          <DsasButton
            variant="secondary"
            label="Copier en tant qu'IFRAME AVEC SCROLL INTELLIGENT"
            onClick={onCopyIframeSmartScroll}
            disabled={isButtonDisabled()}
          />
        </Box>
      )}

      <Box className="large-separator"></Box>

      <Box className="centered-wrapper">
        <DsasButton variant="secondary" label="Copier l'URL" onClick={onCopyURL} />
      </Box>

{isActu && ( <>
      <Box className="large-separator"></Box>

      {/* Export Word section (no nested Dialog) */}
      <Box className="centered-wrapper">
        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            userSelect: "none",
          }}
          onClick={() => setExportOpen((v) => !v)}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Export Word
          </Typography> */}
          <Box className="centered-wrapper">
            <DsasButton variant="secondary" customCss={{marginRight: '0'}} label="Export Word" onClick={() => setExportOpen((v) => !v)} />
          </Box>
          {exportOpen ? <ExpandLess /> : <ExpandMore />}
        </Box>
<Box>
        <Collapse in={exportOpen} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <Box className={'padding-wrapper'}>
            <FormGroup>
              {["all", ...EXPORT_TYPES].map((key) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={exportSelection[key]}
                      onChange={handleExportCheckbox(key)}
                    />
                  }
                  label={EXPORT_TYPE_LABELS[key]}
                />
              ))}
            </FormGroup>
            </Box>

            <Box className='centered-wrapper' sx={{ display: "flex", gap: 1, mt: 1, mb: 2 }}>
              <Button variant="outlined" onClick={() => setExportOpen(false)}>
                Fermer
              </Button>
              <Button variant="contained" onClick={onExportWord}>
                Télécharger
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box> 
      </>
      )}
    </Dialog>
  );
}

SimpleDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  url: PropTypes.string.isRequired,
  isActu: PropTypes.bool,
  idLive: PropTypes.string,
  live: PropTypes.object,
  tags: PropTypes.array,
  selectedTags: PropTypes.array,
  onTagToggle: PropTypes.func,
};

LiveExport.propTypes = {
  idLive: PropTypes.string.isRequired,
  isActu: PropTypes.bool,
  live: PropTypes.object,
};

export default LiveExport;